import React, { useState } from 'react'
import { useNotifications } from '../../contexts/NotificationContext'

const NotificationSettings: React.FC = () => {
  const { 
    settings, 
    permission, 
    isSupported, 
    updateSettings, 
    requestPermission,
    sendNotification,
    emailRecipients,
    addEmailRecipient,
    removeEmailRecipient,
    updateEmailRecipient,
    sendTestEmail
  } = useNotifications()
  
  const [isLoading, setIsLoading] = useState(false)
  const [showAddEmail, setShowAddEmail] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [isTestingEmail, setIsTestingEmail] = useState(false)

  // Expandible sections state
  const [pushNotificationsExpanded, setPushNotificationsExpanded] = useState(true)
  const [emailNotificationsExpanded, setEmailNotificationsExpanded] = useState(true)

  const handleEnableNotifications = async () => {
    setIsLoading(true)
    try {
      const granted = await requestPermission()
      if (granted) {
        console.log('Notifications enabled successfully')
      }
    } catch (error) {
      console.error('Error enabling notifications:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSettingChange = async (key: keyof typeof settings, value: any) => {
    try {
      await updateSettings({ [key]: value })
    } catch (error) {
      console.error('Error updating setting:', error)
    }
  }

  const testNotification = () => {
    console.log('🧪 Testing notification...')
    console.log('Settings:', settings)
    console.log('Permission:', permission)
    console.log('Is supported:', isSupported)
    
    // Force test notification even if settings are disabled
    if (permission === 'granted' && isSupported) {
      try {
        const testOptions = {
          body: 'Las notificaciones están funcionando correctamente',
          tag: 'test-notification',
          icon: '/favicon.ico',
          requireInteraction: true
        }
        
        console.log('Sending test notification with options:', testOptions)
        
        // Direct notification without context validations for testing
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          console.log('Sending via service worker...')
          navigator.serviceWorker.controller.postMessage({
            type: 'SHOW_NOTIFICATION',
            title: '🧪 Notificación de Prueba',
            options: testOptions
          })
        } else {
          console.log('Sending direct notification...')
          new Notification('🧪 Notificación de Prueba', testOptions)
        }
        
        console.log('✅ Test notification sent successfully')
      } catch (error) {
        console.error('❌ Error sending test notification:', error)
        alert(`Error enviando notificación: ${error instanceof Error ? error.message : String(error)}`)
      }
    } else {
      console.warn('Cannot send test notification:', { permission, isSupported })
      alert(`No se puede enviar notificación:\n- Permisos: ${permission}\n- Compatibilidad: ${isSupported}`)
    }
  }

  const handleAddEmailRecipient = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEmail) return

    try {
      await addEmailRecipient(newEmail)
      setNewEmail('')
      setShowAddEmail(false)
    } catch (error) {
      console.error('Error adding email recipient:', error)
      alert('Error al agregar destinatario de correo')
    }
  }

  const handleRemoveEmailRecipient = async (recipientId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este destinatario?')) return

    try {
      await removeEmailRecipient(recipientId)
    } catch (error) {
      console.error('Error removing email recipient:', error)
      alert('Error al eliminar destinatario de correo')
    }
  }

  const handleUpdateEmailRecipient = async (recipientId: string, updates: any) => {
    try {
      await updateEmailRecipient(recipientId, updates)
    } catch (error) {
      console.error('Error updating email recipient:', error)
      alert('Error al actualizar destinatario de correo')
    }
  }

  const handleTestEmail = async () => {
    if (emailRecipients.length === 0) {
      alert('No hay destinatarios para probar')
      return
    }

    setIsTestingEmail(true)
    try {
      const success = await sendTestEmail(emailRecipients.map(r => r.id))
      if (success) {
        alert('Correos de prueba enviados exitosamente')
      } else {
        alert('Error al enviar correos de prueba')
      }
    } catch (error) {
      console.error('Error sending test email:', error)
      alert('Error al enviar correos de prueba')
    } finally {
      setIsTestingEmail(false)
    }
  }

  if (!isSupported) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Notificaciones no disponibles
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            Tu navegador no soporta notificaciones web
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Configuración de Notificaciones
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Configura cómo y cuándo recibir recordatorios de tus medicamentos
        </p>
      </div>

      {/* Push Notifications Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <button
          onClick={() => setPushNotificationsExpanded(!pushNotificationsExpanded)}
          className="w-full flex items-center justify-between p-6 text-left focus:outline-none hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center">
            <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM9 13h4a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4a2 2 0 012-2zm8-5a2 2 0 00-2-2H6a2 2 0 00-2 2v4a2 2 0 002 2h9a2 2 0 002-2V8z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                🔔 Notificaciones Push
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Alertas directas en tu dispositivo • {permission === 'granted' ? 'Activadas' : 'Desactivadas'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {permission === 'granted' && (
              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Activas
              </span>
            )}
            <svg 
              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform ${pushNotificationsExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </button>
        
        {pushNotificationsExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-6">
            {/* Permission Status */}
            <div className="mb-6 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Estado de permisos
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {permission === 'granted' && '✅ Notificaciones permitidas'}
                    {permission === 'denied' && '❌ Notificaciones bloqueadas'}
                    {permission === 'default' && '⏳ Permisos no solicitados'}
                  </p>
                </div>
                
                {permission !== 'granted' && (
                  <button
                    onClick={handleEnableNotifications}
                    disabled={isLoading || permission === 'denied'}
                    className="btn btn-primary px-4 py-2 disabled:opacity-50"
                  >
                    {isLoading ? 'Habilitando...' : 'Habilitar'}
                  </button>
                )}
                
                {permission === 'granted' && (
                  <button
                    onClick={testNotification}
                    className="btn btn-secondary px-4 py-2"
                  >
                    🧪 Probar
                  </button>
                )}
              </div>
            </div>

            {/* Notification Settings */}
            <div className="space-y-6">
              {/* Main Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Activar notificaciones
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Habilita o deshabilita todas las notificaciones
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                    disabled={permission !== 'granted'}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Medication Reminders */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Recordatorios de medicamentos
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Recibe notificaciones antes de tomar tus medicamentos
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.medicationReminders}
                    onChange={(e) => handleSettingChange('medicationReminders', e.target.checked)}
                    disabled={!settings.enabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Reminder Time */}
              {settings.medicationReminders && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tiempo de anticipación
                  </label>
                  <select
                    value={settings.reminderTimeMinutes}
                    onChange={(e) => handleSettingChange('reminderTimeMinutes', parseInt(e.target.value))}
                    disabled={!settings.enabled}
                    className="input w-full max-w-xs"
                  >
                    <option value={5}>5 minutos antes</option>
                    <option value={10}>10 minutos antes</option>
                    <option value={15}>15 minutos antes</option>
                    <option value={30}>30 minutos antes</option>
                    <option value={60}>1 hora antes</option>
                  </select>
                </div>
              )}

              {/* Stock Alerts */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Alertas de stock bajo
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Recibe notificaciones cuando se agoten tus medicamentos
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.stockAlerts}
                    onChange={(e) => handleSettingChange('stockAlerts', e.target.checked)}
                    disabled={!settings.enabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Adherence Reports */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Reportes de adherencia
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Recibe resúmenes semanales de tu progreso
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.adherenceReports}
                    onChange={(e) => handleSettingChange('adherenceReports', e.target.checked)}
                    disabled={!settings.enabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Quiet Hours */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                  Horario silencioso
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  No recibir notificaciones durante estas horas
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Desde
                    </label>
                    <input
                      type="time"
                      value={settings.quietHoursStart}
                      onChange={(e) => handleSettingChange('quietHoursStart', e.target.value)}
                      disabled={!settings.enabled}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hasta
                    </label>
                    <input
                      type="time"
                      value={settings.quietHoursEnd}
                      onChange={(e) => handleSettingChange('quietHoursEnd', e.target.value)}
                      disabled={!settings.enabled}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Instructions */}
              {permission === 'denied' && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
                    Notificaciones bloqueadas
                  </h4>
                  <p className="text-sm text-red-600 dark:text-red-300">
                    Para habilitar las notificaciones, debes permitir las notificaciones en la configuración de tu navegador.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Email Notifications Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <button
          onClick={() => setEmailNotificationsExpanded(!emailNotificationsExpanded)}
          className="w-full flex items-center justify-between p-6 text-left focus:outline-none hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center">
            <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                📧 Notificaciones por Correo
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Alertas enviadas a destinatarios específicos • {emailRecipients.length} destinatarios
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {settings.emailNotifications && (
              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Activas
              </span>
            )}
            <svg 
              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform ${emailNotificationsExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </button>
        
        {emailNotificationsExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-6">
            {/* Email Notifications Toggle */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  Activar notificaciones por correo
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Habilita el envío de alertas por correo electrónico
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
              </label>
            </div>

            {settings.emailNotifications && (
              <div className="space-y-6">
                {/* Email Alert Types */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white">
                        Alertas de stock bajo
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Recibe correos cuando se agoten tus medicamentos
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.lowStockEmailAlerts}
                        onChange={(e) => handleSettingChange('lowStockEmailAlerts', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white">
                        Alertas de dosis perdidas
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Recibe correos cuando no tomes medicamentos a tiempo
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.missedDoseEmailAlerts}
                        onChange={(e) => handleSettingChange('missedDoseEmailAlerts', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                </div>

                {/* Email Recipients */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium text-gray-900 dark:text-white">
                      Destinatarios de correo
                    </h5>
                    <button
                      onClick={() => setShowAddEmail(true)}
                      className="btn btn-primary px-3 py-1 text-sm"
                    >
                      + Agregar destinatario
                    </button>
                  </div>

                  {/* Add Email Form */}
                  {showAddEmail && (
                    <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <form onSubmit={handleAddEmailRecipient} className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            className="input w-full"
                            placeholder="ejemplo@correo.com"
                            required
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            className="btn btn-primary px-4 py-2 text-sm"
                          >
                            Agregar
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddEmail(false)
                              setNewEmail('')
                            }}
                            className="btn btn-secondary px-4 py-2 text-sm"
                          >
                            Cancelar
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Recipients List */}
                  {emailRecipients.length > 0 ? (
                    <div className="space-y-3">
                      {emailRecipients.map((recipient) => {
                        const recipientName = recipient.email.split('@')[0]
                        return (
                          <div key={recipient.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h6 className="font-medium text-gray-900 dark:text-white">
                                  {recipientName}
                                </h6>
                                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  Activo
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                {recipient.email}
                              </p>
                              <div className="flex gap-4 mt-2">
                                <label className="flex items-center gap-1 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={recipient.lowStockAlerts}
                                    onChange={(e) => handleUpdateEmailRecipient(recipient.id, {
                                      lowStockAlerts: e.target.checked
                                    })}
                                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                  />
                                  <span className="text-gray-700 dark:text-gray-300">Stock bajo</span>
                                </label>
                                <label className="flex items-center gap-1 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={recipient.missedDoseAlerts}
                                    onChange={(e) => handleUpdateEmailRecipient(recipient.id, {
                                      missedDoseAlerts: e.target.checked
                                    })}
                                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                  />
                                  <span className="text-gray-700 dark:text-gray-300">Dosis perdidas</span>
                                </label>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleRemoveEmailRecipient(recipient.id)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 p-1"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <p>No hay destinatarios de correo configurados</p>
                    </div>
                  )}

                  {/* Test Email Button */}
                  {emailRecipients.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={handleTestEmail}
                        disabled={isTestingEmail}
                        className="btn btn-secondary px-4 py-2 disabled:opacity-50"
                      >
                        {isTestingEmail ? '📧 Enviando...' : '🧪 Probar correos'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationSettings 